import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { DIDCreditsCard } from '../components/admin/DIDCreditsCard';
import {
  AlertTriangle, CheckCircle, Clock, Bug, Monitor, Server,
  Search, RefreshCw, Eye, Check, Trash2, MessageSquare
} from 'lucide-react';

const ErrorDashboard = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    loadErrors();
    loadStats();
  }, [statusFilter, severityFilter, sourceFilter]);

  const loadErrors = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const response = await api.get(`/errors/dashboard?${params.toString()}`);
      setErrors(response.data.errors);
    } catch (error) {
      console.error('Failed to load errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/errors/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleAcknowledge = async (errorId) => {
    try {
      await api.put(`/errors/${errorId}/acknowledge`);
      toast({ title: 'Success', description: 'Error acknowledged' });
      loadErrors();
      loadStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to acknowledge', variant: 'destructive' });
    }
  };

  const handleResolve = async (errorId) => {
    try {
      await api.put(`/errors/${errorId}/resolve?note=${encodeURIComponent(resolveNote)}`);
      toast({ title: 'Success', description: 'Error resolved' });
      setDetailOpen(false);
      setResolveNote('');
      loadErrors();
      loadStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to resolve', variant: 'destructive' });
    }
  };

  const handleDelete = async (errorId) => {
    if (!window.confirm('Are you sure you want to delete this error?')) return;
    try {
      await api.delete(`/errors/${errorId}`);
      toast({ title: 'Success', description: 'Error deleted' });
      loadErrors();
      loadStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      error: 'bg-orange-100 text-orange-800 border-orange-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      acknowledged: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bug className="h-6 w-6 text-red-500" />
              Error Dashboard
            </h1>
            <p className="text-gray-600">Monitor and manage application errors</p>
          </div>
          <Button onClick={() => { loadErrors(); loadStats(); }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* D-ID video credits */}
        <div className="mb-6">
          <DIDCreditsCard />
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">New Errors</p>
                    <p className="text-3xl font-bold">{stats.by_status?.new || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Critical</p>
                    <p className="text-3xl font-bold">{stats.by_severity?.critical || 0}</p>
                  </div>
                  <Bug className="h-8 w-8 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Last 24h</p>
                    <p className="text-3xl font-bold">{stats.last_24h || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Resolved</p>
                    <p className="text-3xl font-bold">{stats.by_status?.resolved || 0}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 opacity-80" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status:</span>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Severity:</span>
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Source:</span>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="backend">Backend</SelectItem>
                    <SelectItem value="frontend">Frontend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Errors List */}
        <Card>
          <CardHeader>
            <CardTitle>Errors ({errors.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : errors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">No errors found</p>
                <p className="text-sm">Your application is running smoothly!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {errors.map((error) => (
                  <div
                    key={error._id}
                    className="p-4 border rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {error.source === 'backend' ? (
                            <Server className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Monitor className="h-4 w-4 text-purple-500" />
                          )}
                          <Badge className={getSeverityBadge(error.severity)}>
                            {error.severity}
                          </Badge>
                          <Badge className={getStatusBadge(error.status)}>
                            {error.status}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            x{error.count}
                          </span>
                        </div>
                        <p className="font-mono text-sm text-gray-900 truncate">
                          {error.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {error.endpoint} • Last seen: {formatDate(error.last_seen)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedError(error);
                            setDetailOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {error.status === 'new' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledge(error._id)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(error._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Error Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedError && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Bug className="h-5 w-5 text-red-500" />
                    Error Details
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Meta */}
                  <div className="flex flex-wrap gap-2">
                    <Badge className={getSeverityBadge(selectedError.severity)}>
                      {selectedError.severity}
                    </Badge>
                    <Badge className={getStatusBadge(selectedError.status)}>
                      {selectedError.status}
                    </Badge>
                    <Badge variant="outline">
                      {selectedError.source}
                    </Badge>
                    <Badge variant="outline">
                      {selectedError.count} occurrences
                    </Badge>
                  </div>

                  {/* Message */}
                  <div>
                    <h4 className="font-semibold mb-2">Error Message</h4>
                    <pre className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-x-auto text-sm">
                      {selectedError.message}
                    </pre>
                  </div>

                  {/* Stack Trace */}
                  {selectedError.stack && (
                    <div>
                      <h4 className="font-semibold mb-2">Stack Trace</h4>
                      <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto text-xs max-h-48">
                        {selectedError.stack}
                      </pre>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">First Seen</h4>
                      <p className="text-sm text-gray-600">{formatDate(selectedError.first_seen)}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Last Seen</h4>
                      <p className="text-sm text-gray-600">{formatDate(selectedError.last_seen)}</p>
                    </div>
                  </div>

                  {/* Endpoint */}
                  {selectedError.endpoint && (
                    <div>
                      <h4 className="font-semibold mb-2">Endpoint</h4>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {selectedError.method} {selectedError.endpoint}
                      </code>
                    </div>
                  )}

                  {/* Recent Occurrences */}
                  {selectedError.occurrences && selectedError.occurrences.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Recent Occurrences</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedError.occurrences.slice(-5).reverse().map((occ, idx) => (
                          <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                            <span className="text-gray-500">{formatDate(occ.timestamp)}</span>
                            {occ.user_email && (
                              <span className="ml-2 text-gray-700">• {occ.user_email}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedError.status !== 'resolved' && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-2">Resolve Error</h4>
                      <Textarea
                        placeholder="Add a note about how this was resolved..."
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                        rows={2}
                        className="mb-2"
                      />
                      <Button
                        onClick={() => handleResolve(selectedError._id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Resolved
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ErrorDashboard;
